import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../../store/store';
import {
  IncomingCallsRequest,
  incomingCallsStatus,
  selectIncomingCalls,
  totalIncomingCalls,
} from '../../../../store/reducers/IncomingCallsSlice';
import { selectClinics } from '../../../../store/reducers/ClinicSlice';
import { selectSpecializations } from '../../../../store/reducers/SpecializationDoctorSlice';
import { selectSurnameDoctor } from '../../../../store/reducers/surnameDoctor';
import { ReportRequest } from '../../../../store/reducers/ReportSlice';
import { phone } from '../../../../utils/phone';
import { formatDateTime } from '../../../../utils';
import { CurrentIncomingCalls } from '../../../../models/IncomingCalls/IncomingCalls';
import { BlockNumberItem } from '../../../../models/BlockNumbers/BlockNumbers';
import { ResultsTypes, StatusTypes } from '../types';

const PAGE_SIZE: number = 20;

export const useIncomingCallsState = () => {
  const incomingCalls = useAppSelector(selectIncomingCalls);
  const clinics = useAppSelector(selectClinics);
  const specializations = useAppSelector(selectSpecializations);
  const surnameDoctors = useAppSelector(selectSurnameDoctor);
  const totalCount = useAppSelector(totalIncomingCalls);
  const status = useAppSelector(incomingCallsStatus);
  const [index, setIndex] = useState<number>();
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [callRequest, setCallRequest] = useState<IncomingCallsRequest>({
    'Filter.From': dayjs().format('YYYY-MM-DD'),
    'Filter.To': dayjs().format('YYYY-MM-DD'),
    PageIndex: 0,
    PageSize: pageSize,
    SortKeySelector: 'state',
    'Filter.TextSearch': '',
    'Filter.ClinicIds': [] as string[],
    'Filter.States': [] as string[],
    'Filter.AttemptStates': [] as string[],
    'Filter.Results': [] as string[],
    'Filter.Types': [] as string[],
    SortOrder: 'Asc',
  } as IncomingCallsRequest);

  const [callRequestReport, setCallRequestReport] = useState<ReportRequest>({
    From: dayjs().format('YYYY-MM-DD'),
    To: dayjs().format('YYYY-MM-DD'),
  });

  const totalBlockCount = 0;

  let resultBlock: BlockNumberItem[] = [];

  const currentResultBlock: CurrentIncomingCalls[] = useMemo(() => {
    if (!resultBlock) return [];
    return resultBlock.map((call: BlockNumberItem, index: number) => ({
      id: call.id,
      phone: phone(call.phone),
      patientFullname: call.patientFullname || '',
      date: formatDateTime(call.created),
      beginRaw: call.created,
      recording: '',
      indexId: index + totalCount,
    }));
  }, [resultBlock, totalCount]);
  let total = totalCount + totalBlockCount;

  const currentIncomingCalls: CurrentIncomingCalls[] = useMemo(() => {
    if (!incomingCalls) return [];

    return incomingCalls.map((call, index) => {
      const doctor = call.doctor;
      const doctorName = doctor
        ? [
            doctor.surname,
            doctor.givenName?.[0] ? `${doctor.givenName[0]}.` : '',
            doctor.patronym?.[0] ? `${doctor.patronym[0]}.` : '',
          ]
            .filter(Boolean)
            .join(' ')
        : undefined;
      const patientFullname = [call.surname, call.givenName, call.patronym]
        .filter(Boolean)
        .join(' ');

      return {
        id: call.id,
        phone: phone(call.phone),
        patientFullname,
        date: formatDateTime(call.begin ?? call.callDateTime ?? ''),
        beginRaw: call.begin ?? call.callDateTime ?? '',
        clinic: call.clinic?.name || '',
        type: call.type,
        status: call.state as StatusTypes | undefined,
        result: call.result as ResultsTypes | undefined,
        recording: '',
        indexId: index,
        doctorName,
        doctorSpecialization: undefined,
      };
    });
  }, [incomingCalls, surnameDoctors]);

  const allCalls: CurrentIncomingCalls[] = useMemo(
    () => [...currentIncomingCalls, ...currentResultBlock],
    [currentIncomingCalls, currentResultBlock],
  );

  return {
    clinics,
    specializations,
    surnameDoctors,
    totalCount,
    status,
    setIndex,
    pageSize,
    setPageSize,
    callRequest,
    setCallRequest,
    callRequestReport,
    setCallRequestReport,
    total,
    currentIncomingCalls,
    allCalls,
  };
};
